import SafeModal from 'components/SafeModal';
import Papa from 'papaparse';
import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button, ModalHeader } from 'reactstrap';
import { Person } from 'types';
import ConfirmAPI from 'utils/api/ConfirmAPI';
import ColumnMappingStep from './ColumnMappingStep';
import ConfirmOverwriteStep from './ConfirmOverwriteStep';
import ConflictStep from './ConflictStep';
import FileUploadStep from './FileUploadStep';
import PermissionsStep from './PermissionsStep';
import ResultsStep from './ResultsStep';
import SubmittingStep from './SubmittingStep';
import {
  ColumnMapping,
  ColumnVisibility,
  OwnedColumn,
  UploadResult,
  VisibilityOption,
} from './types';

const PAGE_SIZE = 10;

type Step =
  | 'idle'
  | 'file_upload'
  | 'column_mapping'
  | 'permissions'
  | 'submitting'
  | 'results'
  | 'conflict'
  | 'confirm_overwrite';

interface CustomerCampaignDataUploadProps {
  campaignId: number;
  organizationId: number;
  currentProxyPerson?: Person | null;
}

const CustomerCampaignDataUpload: FC<CustomerCampaignDataUploadProps> = ({
  campaignId,
  organizationId,
  currentProxyPerson,
}) => {
  const { formatMessage } = useIntl();

  // When an admin is impersonating (proxying) another user, forward the
  // `proxy` param so these endpoints resolve the impersonated user rather
  // than the admin — matching how the surrounding dashboard loads its data.
  // See get_permissioned_user (admin/api/models.py). Empty when not proxying.
  const proxyParams = useMemo(
    () =>
      currentProxyPerson?.email ? { proxy: currentProxyPerson.email } : {},
    [currentProxyPerson?.email]
  );

  const visibilityOptions: VisibilityOption[] = useMemo(
    () => [
      {
        value: 'owners',
        label: (
          <FormattedMessage
            id="app.custom_campaign_data.visibility.owners"
            defaultMessage="Owners"
          />
        ),
      },
      {
        value: 'hr_admins',
        label: (
          <FormattedMessage
            id="app.custom_campaign_data.visibility.hr_admins"
            defaultMessage="Only HR admins (not managers)"
          />
        ),
      },
      {
        value: 'everyone',
        label: (
          <FormattedMessage
            id="app.custom_campaign_data.visibility.everyone"
            defaultMessage="Managers and above and HR admins"
          />
        ),
      },
    ],
    []
  );
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [step, setStep] = useState<Step>('idle');
  const [error, setError] = useState('');
  const [loadingColumns, setLoadingColumns] = useState(false);
  const [savingVisibility, setSavingVisibility] = useState(false);
  const [visibilityDirty, setVisibilityDirty] = useState(false);
  const [visibilitySaved, setVisibilitySaved] = useState(false);
  const [deletingColumnKey, setDeletingColumnKey] = useState<string | null>(
    null
  );

  // Parsed CSV data (2D array — rows of cells, first row = headers)
  const [rawRows, setRawRows] = useState<string[][]>([]);

  // Column mapping state
  const [columnMappings, setColumnMappings] = useState<ColumnMapping[]>([]);
  const [ownedColumns, setOwnedColumns] = useState<OwnedColumn[]>([]);
  const [otherColumnKeys, setOtherColumnKeys] = useState<Set<string>>(
    new Set()
  );
  const [otherColumnLabels, setOtherColumnLabels] = useState<Set<string>>(
    new Set()
  );
  const [availableIdentifiers, setAvailableIdentifiers] = useState<string[]>(
    []
  );

  // Permissions state
  const [columnVisibilities, setColumnVisibilities] = useState<
    ColumnVisibility[]
  >([]);

  // Results state
  const [uploadResult, setUploadResult] = useState<UploadResult | null>(null);
  const [conflictingColumns, setConflictingColumns] = useState<string[]>([]);
  const [ownExistingColumns, setOwnExistingColumns] = useState<string[]>([]);

  const headers = useMemo(() => rawRows[0] ?? [], [rawRows]);
  const dataRows = useMemo(() => rawRows.slice(1), [rawRows]);

  const reset = useCallback(() => {
    setStep('idle');
    setError('');
    setRawRows([]);
    setColumnMappings([]);
    setOwnedColumns([]);
    setColumnVisibilities([]);
    setUploadResult(null);
    setConflictingColumns([]);
    setOwnExistingColumns([]);
    setOtherColumnKeys(new Set());
    setOtherColumnLabels(new Set());
    setAvailableIdentifiers([]);
    setLoadingColumns(false);
    setSavingVisibility(false);
    setVisibilityDirty(false);
    setVisibilitySaved(false);
    setDeletingColumnKey(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const handleClose = useCallback(() => {
    reset();
  }, [reset]);

  // Fetch owned columns when entering the file_upload step
  useEffect(() => {
    if (step !== 'file_upload') return;
    setLoadingColumns(true);
    ConfirmAPI.sendRequestToConfirm(
      'GET',
      '/customer-campaign-data/columns',
      {
        campaign_id: campaignId,
        organization_id: organizationId,
        ...proxyParams,
      },
      (
        response: {
          owned_columns: OwnedColumn[];
          other_columns: { key: string; label: string }[];
          available_identifiers: string[];
        } | null
      ) => {
        setOwnedColumns(response?.owned_columns ?? []);
        setOtherColumnKeys(
          new Set((response?.other_columns ?? []).map((c) => c.key))
        );
        setOtherColumnLabels(
          new Set(
            (response?.other_columns ?? []).map((c) => c.label.toLowerCase())
          )
        );
        setAvailableIdentifiers(response?.available_identifiers ?? []);
        setLoadingColumns(false);
      }
    );
  }, [step, campaignId, organizationId, proxyParams]);

  const handleOwnedColumnVisibilityChange = useCallback(
    (columnKey: string, visValue: string) => {
      setOwnedColumns((prev) =>
        prev.map((col) =>
          col.key === columnKey ? { ...col, visibility: visValue } : col
        )
      );
      setVisibilityDirty(true);
      setVisibilitySaved(false);
    },
    []
  );

  const handleOwnedColumnLabelChange = useCallback(
    (columnKey: string, newLabel: string) => {
      setOwnedColumns((prev) =>
        prev.map((col) =>
          col.key === columnKey ? { ...col, label: newLabel } : col
        )
      );
      setVisibilityDirty(true);
      setVisibilitySaved(false);
    },
    []
  );

  const handleDeleteOwnedColumn = useCallback(
    (columnKey: string) => {
      setDeletingColumnKey(columnKey);
      setError('');
      ConfirmAPI.sendRequestToConfirm(
        'DELETE',
        '/customer-campaign-data/columns/delete',
        {
          campaign_id: campaignId,
          organization_id: organizationId,
          column_key: columnKey,
          ...proxyParams,
        },
        (data: { status: string } | null, errResp?: { data?: unknown }) => {
          setDeletingColumnKey(null);
          if (!data) {
            const errData = errResp?.data as { error?: string } | undefined;
            setError(errData?.error || 'Failed to delete column.');
            return;
          }
          setOwnedColumns((prev) =>
            prev.filter((col) => col.key !== columnKey)
          );
        }
      );
    },
    [campaignId, organizationId, proxyParams]
  );

  const handleSaveVisibility = useCallback(() => {
    setSavingVisibility(true);
    setError('');
    setVisibilitySaved(false);
    ConfirmAPI.sendRequestToConfirm(
      'PATCH',
      '/customer-campaign-data/columns/visibility',
      {
        campaign_id: campaignId,
        organization_id: organizationId,
        columns: ownedColumns.map((col) => ({
          key: col.key,
          label: col.label,
          visibility: col.visibility,
        })),
        ...proxyParams,
      },
      (data: { status: string } | null, errResp?: { data?: unknown }) => {
        setSavingVisibility(false);
        if (!data) {
          const errData = errResp?.data as { error?: string } | undefined;
          setError(errData?.error || 'Failed to save visibility changes.');
          return;
        }
        setVisibilityDirty(false);
        setVisibilitySaved(true);
      }
    );
  }, [campaignId, organizationId, ownedColumns, proxyParams]);

  // Auto-clear the "Saved" confirmation after 3 seconds
  useEffect(() => {
    if (!visibilitySaved) return;
    const timer = setTimeout(() => setVisibilitySaved(false), 3000);
    return () => clearTimeout(timer);
  }, [visibilitySaved]);

  // Fetch existing columns, auto-detect mappings, and go to column_mapping
  const fetchOwnedColumnsAndProceed = useCallback(
    (rows: string[][]) => {
      ConfirmAPI.sendRequestToConfirm(
        'GET',
        '/customer-campaign-data/columns',
        {
          campaign_id: campaignId,
          organization_id: organizationId,
          ...proxyParams,
        },
        (
          response: {
            owned_columns: OwnedColumn[];
            other_columns: { key: string; label: string }[];
            available_identifiers: string[];
          } | null
        ) => {
          const cols = response?.owned_columns ?? [];
          const otherCols = response?.other_columns ?? [];
          const otherKeys = new Set(otherCols.map((c) => c.key));
          const otherLabels = new Set(
            otherCols.map((c) => c.label.toLowerCase())
          );
          const identifiers = response?.available_identifiers ?? [];
          setOwnedColumns(cols);
          setOtherColumnKeys(otherKeys);
          setOtherColumnLabels(otherLabels);
          setAvailableIdentifiers(identifiers);
          setRawRows(rows);

          // All taken keys (owned + other) for unique key generation
          const allTakenKeys = new Set(
            cols.map((c) => c.key).concat(Array.from(otherKeys))
          );

          // Auto-detect mappings from header row (first row)
          const hdrs = rows[0] ?? [];
          const mappings: ColumnMapping[] = hdrs.map((header) => {
            const lowerHeader = header.toLowerCase().trim();
            const normalizedKey = lowerHeader.replace(/\s+/g, '_');

            if (
              identifiers.includes('email') &&
              (lowerHeader === 'email' || lowerHeader === 'email address')
            ) {
              return { type: 'identifier', identifierKey: 'email' };
            }
            if (
              identifiers.includes('external_id') &&
              (lowerHeader === 'external_id' || lowerHeader === 'external id')
            ) {
              return { type: 'identifier', identifierKey: 'external_id' };
            }

            // Only auto-map to owned columns
            const matchingOwned = cols.find(
              (col) =>
                col.key === normalizedKey ||
                col.label.toLowerCase() === lowerHeader ||
                col.key === lowerHeader
            );
            if (matchingOwned) {
              return { type: 'existing', existingKey: matchingOwned.key };
            }

            // Generate a unique key that doesn't conflict with other users' columns
            let newKey = normalizedKey;
            if (allTakenKeys.has(newKey)) {
              let suffix = 2;
              while (allTakenKeys.has(`${normalizedKey}_${suffix}`)) {
                suffix++;
              }
              newKey = `${normalizedKey}_${suffix}`;
            }
            allTakenKeys.add(newKey);

            return { type: 'new', newKey, label: header };
          });

          setColumnMappings(mappings);
          setStep('column_mapping');
        }
      );
    },
    [campaignId, organizationId, proxyParams]
  );

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      Papa.parse<string[]>(file, {
        header: false,
        skipEmptyLines: true,
        worker: true,
        complete: (result) => {
          const rows = result.data;

          if (rows.length < 2) {
            setError(
              formatMessage({
                id: 'app.custom_campaign_data.error.min_rows',
                defaultMessage:
                  'CSV must have at least a header row and one data row.',
              })
            );
            return;
          }

          setError('');
          fetchOwnedColumnsAndProceed(rows);
        },
      });
    },
    [formatMessage, fetchOwnedColumnsAndProceed]
  );

  const handleColumnMappingChange = useCallback(
    (colIndex: number, value: string) => {
      setColumnMappings((prev) => {
        const next = [...prev];
        if (value === '__ignore__') {
          next[colIndex] = { type: 'ignore' };
        } else if (value === '__email__') {
          next[colIndex] = { type: 'identifier', identifierKey: 'email' };
        } else if (value === '__external_id__') {
          next[colIndex] = {
            type: 'identifier',
            identifierKey: 'external_id',
          };
        } else if (value === '__new__') {
          const header = headers[colIndex] ?? `column_${colIndex}`;
          const baseKey = header.toLowerCase().replace(/\s+/g, '_');
          const ownedKeys = new Set(ownedColumns.map((c) => c.key));
          const isKeyTaken = (k: string) =>
            otherColumnKeys.has(k) || ownedKeys.has(k);
          let newKey = baseKey;
          if (isKeyTaken(newKey)) {
            let suffix = 2;
            while (isKeyTaken(`${baseKey}_${suffix}`)) {
              suffix++;
            }
            newKey = `${baseKey}_${suffix}`;
          }
          next[colIndex] = { type: 'new', newKey, label: header };
        } else {
          // Existing column key
          next[colIndex] = { type: 'existing', existingKey: value };
        }
        return next;
      });
    },
    [headers, otherColumnKeys, ownedColumns]
  );

  const getMappingValue = useCallback((mapping: ColumnMapping): string => {
    switch (mapping.type) {
      case 'identifier':
        return `__${mapping.identifierKey}__`;
      case 'existing':
        return mapping.existingKey;
      case 'new':
        return '__new__';
      case 'ignore':
        return '__ignore__';
    }
  }, []);

  // Validation: all available identifiers required, no duplicate identifier types, no duplicate existing mappings
  const mappingValidation = useMemo(() => {
    const identifiers = columnMappings.filter((m) => m.type === 'identifier');
    const identifierKeys = identifiers.map(
      (m) => (m as { type: 'identifier'; identifierKey: string }).identifierKey
    );
    const duplicateIdentifiers = identifierKeys.filter(
      (key, i) => identifierKeys.indexOf(key) !== i
    );
    const dataColumns = columnMappings.filter(
      (m) => m.type === 'existing' || m.type === 'new'
    );
    const existingMapped = columnMappings
      .filter((m) => m.type === 'existing')
      .map((m) => (m as { type: 'existing'; existingKey: string }).existingKey);
    const duplicates = existingMapped.filter(
      (key, i) => existingMapped.indexOf(key) !== i
    );

    if (identifierKeys.length === 0) {
      return {
        valid: false,
        error: formatMessage({
          id: 'app.custom_campaign_data.mapping.error.missing_any_identifier',
          defaultMessage:
            'You must map at least one identifier column (email or external ID).',
        }),
      };
    }
    if (duplicateIdentifiers.length > 0) {
      return {
        valid: false,
        error: formatMessage({
          id: 'app.custom_campaign_data.mapping.error.duplicate_identifier',
          defaultMessage:
            'Multiple columns are mapped to the same identifier type.',
        }),
      };
    }
    if (dataColumns.length === 0) {
      return {
        valid: false,
        error: formatMessage({
          id: 'app.custom_campaign_data.mapping.error.no_data_columns',
          defaultMessage:
            'You must map at least one data column (existing or new).',
        }),
      };
    }
    if (duplicates.length > 0) {
      return {
        valid: false,
        error: formatMessage({
          id: 'app.custom_campaign_data.mapping.error.duplicate_mapping',
          defaultMessage:
            'Multiple CSV columns are mapped to the same existing column.',
        }),
      };
    }
    return { valid: true, error: '' };
  }, [columnMappings, formatMessage]);

  // Build visibility configs from column mappings
  const initializePermissions = useCallback(() => {
    const visibilities: ColumnVisibility[] = [];

    columnMappings.forEach((mapping) => {
      if (mapping.type === 'existing') {
        const existing = ownedColumns.find(
          (c) => c.key === mapping.existingKey
        );
        if (existing) {
          visibilities.push({
            key: existing.key,
            label: existing.label,
            visibility: existing.visibility,
            isNew: false,
          });
        }
      } else if (mapping.type === 'new') {
        visibilities.push({
          key: mapping.newKey,
          label: mapping.label,
          visibility: 'hr_admins',
          isNew: true,
        });
      }
    });

    setColumnVisibilities(visibilities);
    setStep('permissions');
  }, [columnMappings, ownedColumns]);

  const handleVisibilityChange = useCallback(
    (columnKey: string, visValue: string) => {
      setColumnVisibilities((prev) =>
        prev.map((col) => {
          if (col.key !== columnKey) return col;
          return { ...col, visibility: visValue };
        })
      );
    },
    []
  );

  const handleColumnVisibilityLabelChange = useCallback(
    (columnKey: string, newLabel: string) => {
      setColumnVisibilities((prev) =>
        prev.map((col) => {
          if (col.key !== columnKey) return col;
          return { ...col, label: newLabel };
        })
      );
    },
    []
  );

  // Build final data and submit
  const submitUpload = useCallback(
    (overwrite: boolean) => {
      // Ensure at least one identifier is mapped
      const hasIdentifier = columnMappings.some((m) => m.type === 'identifier');
      if (!hasIdentifier) return;

      // Build columns payload
      const columns = columnVisibilities.map((cv) => ({
        key: cv.key,
        label: cv.label,
        visibility: cv.visibility,
      }));

      // Build data payload — map CSV rows using column mappings
      const data = dataRows.map((row) => {
        const mapped: Record<string, string> = {};

        columnMappings.forEach((mapping, colIdx) => {
          if (mapping.type === 'identifier') {
            mapped[mapping.identifierKey] = row[colIdx] ?? '';
          } else if (mapping.type === 'existing') {
            mapped[mapping.existingKey] = row[colIdx] ?? '';
          } else if (mapping.type === 'new') {
            mapped[mapping.newKey] = row[colIdx] ?? '';
          }
        });

        return mapped;
      });

      setStep('submitting');
      setError('');

      ConfirmAPI.sendRequestToConfirm(
        'POST',
        '/customer-campaign-data/upload',
        {
          organization_id: organizationId,
          campaign_id: campaignId,
          columns,
          data,
          overwrite,
          ...proxyParams,
        },
        (data: UploadResult | null, errorResponse?: { data?: unknown }) => {
          if (!data) {
            const errData = errorResponse?.data as
              | {
                  error?: string;
                  error_code?: string;
                  conflicting_columns?: string[];
                  own_existing_columns?: string[];
                }
              | undefined;
            if (errData?.error_code === 'overwrite_owned') {
              setOwnExistingColumns(errData.own_existing_columns ?? []);
              setStep('confirm_overwrite');
            } else if (errData?.error_code === 'overwrite_others') {
              setConflictingColumns(errData.conflicting_columns ?? []);
              setStep('conflict');
            } else {
              setError(errData?.error || 'Upload failed.');
              setStep('permissions');
            }
            return;
          }
          setUploadResult(data);
          setStep('results');
        }
      );
    },
    [
      columnMappings,
      columnVisibilities,
      dataRows,
      organizationId,
      campaignId,
      proxyParams,
    ]
  );

  const handleSubmit = useCallback(() => submitUpload(false), [submitUpload]);
  const handleOverwrite = useCallback(() => submitUpload(true), [submitUpload]);

  const handleOpenModal = useCallback(() => setStep('file_upload'), []);
  const handleBackToFileUpload = useCallback(() => setStep('file_upload'), []);
  const handleBackToColumnMapping = useCallback(
    () => setStep('column_mapping'),
    []
  );
  const handleBackToPermissions = useCallback(() => setStep('permissions'), []);

  const modalIsOpen = step !== 'idle';

  return (
    <>
      <Button
        color="light"
        className="btn btn-light btn-sm"
        onClick={handleOpenModal}
      >
        <i className="fe fe-file-text me-2" />
        <FormattedMessage
          id="app.custom_campaign_data.button"
          defaultMessage="Custom data"
        />
      </Button>

      <SafeModal isOpen={modalIsOpen} toggle={handleClose} size="xl">
        <ModalHeader toggle={handleClose}>
          <FormattedMessage
            id="app.custom_campaign_data.modal.title"
            defaultMessage="Upload custom cycle data"
          />
        </ModalHeader>
        {step === 'file_upload' && (
          <FileUploadStep
            error={error}
            loadingColumns={loadingColumns}
            ownedColumns={ownedColumns}
            savingVisibility={savingVisibility}
            visibilityDirty={visibilityDirty}
            visibilitySaved={visibilitySaved}
            visibilityOptions={visibilityOptions}
            fileInputRef={fileInputRef}
            deletingColumnKey={deletingColumnKey}
            onOwnedColumnVisibilityChange={handleOwnedColumnVisibilityChange}
            onOwnedColumnLabelChange={handleOwnedColumnLabelChange}
            onDeleteOwnedColumn={handleDeleteOwnedColumn}
            onSaveVisibility={handleSaveVisibility}
            onFileChange={handleFileChange}
          />
        )}
        {step === 'column_mapping' && (
          <ColumnMappingStep
            headers={headers}
            dataRows={dataRows}
            columnMappings={columnMappings}
            ownedColumns={ownedColumns}
            availableIdentifiers={availableIdentifiers}
            mappingValidation={mappingValidation}
            pageSize={PAGE_SIZE}
            onMappingChange={handleColumnMappingChange}
            getMappingValue={getMappingValue}
            onBack={handleBackToFileUpload}
            onNext={initializePermissions}
          />
        )}
        {step === 'permissions' && (
          <PermissionsStep
            error={error}
            columnVisibilities={columnVisibilities}
            visibilityOptions={visibilityOptions}
            otherColumnLabels={otherColumnLabels}
            onVisibilityChange={handleVisibilityChange}
            onLabelChange={handleColumnVisibilityLabelChange}
            onBack={handleBackToColumnMapping}
            onSubmit={handleSubmit}
          />
        )}
        {step === 'submitting' && <SubmittingStep />}
        {step === 'results' && (
          <ResultsStep uploadResult={uploadResult} onClose={handleClose} />
        )}
        {step === 'confirm_overwrite' && (
          <ConfirmOverwriteStep
            ownExistingColumns={ownExistingColumns}
            ownedColumns={ownedColumns}
            onCancel={handleBackToPermissions}
            onOverwrite={handleOverwrite}
          />
        )}
        {step === 'conflict' && (
          <ConflictStep
            conflictingColumns={conflictingColumns}
            onBack={handleBackToColumnMapping}
            onClose={handleClose}
          />
        )}
      </SafeModal>
    </>
  );
};

export default CustomerCampaignDataUpload;
